import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AppLayout, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowDown, Check, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import CreditMeter from '../components/CreditMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LADDER = ['plus', 'pro', 'max', 'elite'];
const HUE = { plus: '#22d3ee', pro: '#a78bfa', max: '#34d399', elite: '#fbbf24' };
const PLAN_LABEL = { trial: 'Free trial', plus: 'Plus', pro: 'Pro', max: 'Max', elite: 'Elite' };
const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function BillingPage() {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [annual, setAnnual] = useState(user?.billing_interval === 'annual');
  const [busy, setBusy] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const p = await axios.get(`${API}/plans`);
    setPlans(p.data.plans || []);
    setPacks(p.data.credit_packs || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirm = useCallback(async (sessionId, attempt = 0) => {
    if (attempt > 8) { setVerifying(false); toast.info('Still confirming with Stripe, refresh in a moment.'); return; }
    setVerifying(true);
    try {
      const r = await axios.get(`${API}/subscription/confirm?session_id=${sessionId}`, getAuthHeader());
      if (r.data.status === 'paid') {
        setVerifying(false);
        toast.success(r.data.credits_added
          ? `${r.data.credits_added.toLocaleString()} credits added.`
          : `You're on ${r.data.plan?.toUpperCase()}, ${r.data.credits?.toLocaleString()} credits ready.`);
        await refreshUser?.();
        load();
        return;
      }
    } catch { /* keep polling */ }
    setTimeout(() => confirm(sessionId, attempt + 1), 2000);
  }, [getAuthHeader, refreshUser, load]);

  useEffect(() => {
    const sid = params.get('session_id');
    if (sid) confirm(sid);
  }, [params, confirm]);

  const current = user?.plan || 'trial';
  const currentIdx = LADDER.indexOf(current);

  const choose = async (plan) => {
    setBusy(plan);
    try {
      const r = await axios.post(`${API}/subscription/checkout`,
        { plan, interval: annual ? 'annual' : 'monthly', origin_url: window.location.origin },
        getAuthHeader());
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not open checkout.'); setBusy(''); }
  };

  const buyPack = async (pack_key) => {
    setBusy(pack_key);
    try {
      const r = await axios.post(`${API}/subscription/credit-pack`,
        { pack_key, origin_url: window.location.origin }, getAuthHeader());
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not open checkout.'); setBusy(''); }
  };

  const downgrade = async (plan) => {
    try {
      const r = await axios.post(`${API}/subscription/downgrade`, { plan }, getAuthHeader());
      toast.success(r.data.message);
      await refreshUser?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not schedule that.'); }
  };

  if (loading) return <AppLayout><div className="py-24 flex justify-center"><LoadingSpinner /></div></AppLayout>;

  return (
    <AppLayout>
      <PageHeader title="Plan and AI capacity"
        description="Your plan, what's left this cycle, and how to change it. Nothing you've created is ever affected." />

      {verifying && (
        <div className="glass-tint glass-cyan p-4 mb-6 text-sm text-cyan-200" data-testid="billing-verifying">
          Confirming your payment with Stripe…
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <CreditMeter />

          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-outfit text-xl font-semibold text-white">
                {currentIdx >= 0 ? 'Change your capacity' : 'Choose a plan'}
              </h2>
              <div className="inline-flex items-center gap-1 p-1 rounded-full border border-white/12 bg-white/[0.04]">
                {[['Monthly', false], ['Annual', true]].map(([label, isA]) => (
                  <button key={label} onClick={() => setAnnual(isA)} data-testid={`billing-toggle-${label.toLowerCase()}`}
                    className="px-4 py-1.5 rounded-full text-xs transition-all duration-200"
                    style={annual === isA ? { background: 'linear-gradient(90deg,#22d3ee,#a78bfa)', color: '#04121a' }
                      : { color: '#cbd5e1' }}>{label}</button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {plans.filter(p => p.key !== 'trial').map(p => {
                const isCurrent = p.key === current;
                const idx = LADDER.indexOf(p.key);
                const isDowngrade = currentIdx >= 0 && idx < currentIdx;
                const hue = HUE[p.key];
                return (
                  <div key={p.key} className="glass-tint fp-rail p-5" data-testid={`billing-plan-${p.key}`}
                    style={isCurrent ? { borderColor: `${hue}66`, boxShadow: `0 0 50px -18px ${hue}` } : undefined}>
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="font-outfit text-lg font-semibold text-white">{p.name}</p>
                      {isCurrent && <span className="text-[10px] uppercase tracking-wider" style={{ color: hue }}>Current</span>}
                    </div>
                    <p className="font-outfit text-2xl font-bold text-white mt-2 fp-num">
                      {money(annual ? p.price_annual : p.price_monthly)}
                      <span className="text-xs font-normal text-slate-300">{annual ? '/year' : '/month'}</span>
                    </p>
                    {annual && <p className="text-[11px] text-slate-300">{money(p.effective_monthly_on_annual)}/month equivalent</p>}
                    <p className="text-sm mt-2" style={{ color: hue }}>
                      {p.monthly_credits?.toLocaleString()} credits every month
                    </p>
                    <p className="text-xs text-slate-300 mt-2">{p.positioning}</p>

                    {isCurrent ? (
                      <p className="text-xs text-slate-300 mt-4 flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5" style={{ color: hue }} />You're on this plan
                      </p>
                    ) : isDowngrade ? (
                      <Button variant="outline" onClick={() => downgrade(p.key)} data-testid={`downgrade-${p.key}`}
                        className="w-full mt-4 rounded-full border-slate-700 text-slate-300 text-xs">
                        <ArrowDown className="w-3.5 h-3.5 mr-1.5" />Move down next cycle
                      </Button>
                    ) : (
                      <Button onClick={() => choose(p.key)} disabled={busy === p.key} data-testid={`upgrade-${p.key}`}
                        className="w-full mt-4 rounded-full text-slate-900 font-semibold text-xs"
                        style={{ background: `linear-gradient(90deg, ${hue}, #22d3ee)` }}>
                        {busy === p.key ? 'Opening…' : currentIdx >= 0 ? `Upgrade to ${p.name}` : `Choose ${p.name}`}
                        <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass-tint glass-emerald p-5" data-testid="billing-packs">
            <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/85">Need more this month</p>
            <p className="text-xs text-slate-300 mt-2">
              Add capacity in $10 steps. Top-ups apply to this cycle and are used after your allowance.
            </p>
            <div className="space-y-2 mt-4">
              {packs.map(pk => (
                <button key={pk.key} onClick={() => buyPack(pk.key)} disabled={busy === pk.key}
                  data-testid={`buy-${pk.key}`} className="fp-tile p-3 w-full text-left">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-white">{pk.credits.toLocaleString()} credits</span>
                    <span className="text-sm text-emerald-300 fp-num">${pk.price_usd}</span>
                  </div>
                  {pk.note && <p className="text-[11px] text-amber-300/90 mt-1">{pk.note}</p>}
                </button>
              ))}
            </div>
          </div>

          <div className="glass-tint p-5" data-testid="billing-summary">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-300 flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5" />Your billing
            </p>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-300">Plan</span>
                <span className="text-white">{PLAN_LABEL[current] || 'Free trial'}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-300">Billing</span>
                <span className="text-white capitalize">{user?.billing_interval || '-'}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-300">Status</span>
                <span className="text-white capitalize">{(user?.subscription_status || 'trial').replace(/_/g, ' ')}</span>
              </div>
              {user?.pending_plan && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-300">Changing to</span>
                  <span className="text-amber-300">{PLAN_LABEL[user.pending_plan]} next cycle</span>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-300 mt-4">
              Changing plans never affects anything you've created. Your profile, results, reports and
              roadmap stay exactly as they are.
            </p>
            <Link to="/pricing" className="text-xs text-cyan-300 mt-4 inline-flex items-center gap-1.5">
              Compare all plans <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
